import { useCheckResponse } from '@src/hooks';
import { useCreateTicketMutation } from '@src/services/ticketing/ticketingApiSlice';
import { RootState } from '@src/store/store';
import { Button, Form, Input } from 'antd';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';

type FormFields = {
  subject: string;
  message: string;
};

const schema = z.object({
  subject: z.string().nonempty('Required'),
  message: z.string().nonempty('Required'),
});

export function TicketForm() {
  const { t, checkResponse } = useCheckResponse();
  const user = useSelector((state: RootState) => state.user.user);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset: resetFormData,
  } = useForm<FormFields>({
    resolver: zodResolver(schema),
  });

  const [createTicket, { isLoading }] = useCreateTicketMutation();

  const onSubmit = async (formData: FieldValues) => {
    try {
      if (user) {
        const response = await createTicket({
          subject: formData.subject,
          message: formData.message,
          email: user?.email,
        });
        checkResponse(response, 'create', t('pages.ticket.title'));

        if (response.data) {
          resetFormData();
        }
      }
    } catch (error) {
      checkResponse({
        error,
      });
    }
  };
  return (
    <form className='grow flex flex-col' onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name='subject'
        control={control}
        rules={{
          required: true,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <Form.Item
            label={t('forms.subject')}
            layout='vertical'
            required
            className={clsx(
              '[&_.ant-form-item-required]:!text-wl-on-background',
              '[&_.ant-input]:!bg-wl-surface [&_.ant-input]:text-wl-on-background'
            )}
            validateStatus={errors.subject ? 'error' : undefined}
            help={
              errors.subject
                ? 'Should be combination of numbers & alphabets'
                : undefined
            }
            rules={[
              {
                type: 'string',
                required: true,
                message: t('errors.formErrors.required', {
                  name: t('forms.subject'),
                }),
              },
            ]}
          >
            <Input
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              size='large'
              placeholder='Create a subject...'
              className='placeholder:text-wl-on-background placeholder:opacity-50'
            />
          </Form.Item>
        )}
      />
      <Controller
        name='message'
        control={control}
        rules={{
          required: true,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <Form.Item
            label={t('forms.message')}
            layout='vertical'
            required
            className={clsx(
              '[&_.ant-form-item-required]:!text-wl-on-background',
              '[&_.ant-input]:!bg-wl-surface [&_.ant-input]:text-wl-on-background'
            )}
            validateStatus={errors.message ? 'error' : undefined}
            help={
              errors.message
                ? t('errors.formErrors.required', {
                    name: t('forms.message'),
                  })
                : undefined
            }
            rules={[
              {
                type: 'string',
                required: true,
                message: t('errors.formErrors.required', {
                  name: t('forms.message'),
                }),
              },
            ]}
          >
            <Input.TextArea
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              rows={4}
              size='large'
              placeholder='Enter your message...'
              className='placeholder:text-wl-on-background placeholder:opacity-50'
            />
          </Form.Item>
        )}
      />

      <div className='w-full flex justify-between mt-2 space-x-2 item'>
        <Button
          size='large'
          className='w-full btn-primary'
          htmlType='submit'
          loading={isLoading}
        >
          {t('buttons.sendMessage')}
        </Button>
      </div>
    </form>
  );
}
